import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import type {
  AddContractItemAssetInput,
  AddContractItemUserInput,
  ArticlesPage,
  ContractDetail,
  ContractItem,
  ContractItemAsset,
  ContractItemUser,
  ContractItemUserType,
  ContractListItem,
  ContractsOverview,
  CreateContractInput,
  CreateContractItemInput,
  CreatePricingTierInput,
  CreateProductInput,
  CreateTariffComponentInput,
  CreateTariffInput,
  CustomerLite,
  DerivedContractsResponse,
  PricingTier,
  Product,
  ProductDetail,
  SapBusinessPartner,
  Tariff,
  TariffComponent,
  TariffDetail,
  UpdateContractInput,
  UpdateContractItemInput,
  UpdatePricingTierInput,
  UpdateProductInput,
  UpdateTariffComponentInput,
  UpdateTariffInput,
  ContractGroup,
  ContractGroupDetail,
  ContractUserGroup,
  ContractVersion,
  CreateContractGroupInput,
  CreateContractUserGroupInput,
  CreateContractVersionInput,
  PendingUserGroupReduction,
  SetupFeeItem,
  UpdateContractGroupInput,
  UpdateContractUserGroupInput,
} from '../types.ts'

// ─── Overview ───────────────────────────────────────────────────────────────

export function useContractsOverview() {
  const tenantId = useTenantStore((s) => s.activeTenantId)
  return useQuery<ContractsOverview>({
    queryKey: ['contracts', 'overview', tenantId],
    queryFn: () => apiClient.get('/contracts/overview').then((r) => r.data),
  })
}

// ─── Contracts ──────────────────────────────────────────────────────────────

export interface ContractsFilter {
  status?: string
  customerId?: string
  direction?: string
  q?: string
}

export function useContracts(filter: ContractsFilter = {}) {
  const tenantId = useTenantStore((s) => s.activeTenantId)
  return useQuery<ContractListItem[]>({
    queryKey: ['contracts', 'list', filter, tenantId],
    queryFn: () => apiClient.get('/contracts', { params: filter }).then((r) => r.data),
  })
}

export function useContract(id: string | undefined) {
  return useQuery<ContractDetail>({
    queryKey: ['contracts', 'detail', id],
    queryFn: () => apiClient.get(`/contracts/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useDerivedContracts(id: string | undefined) {
  return useQuery<DerivedContractsResponse>({
    queryKey: ['contracts', 'derived', id],
    queryFn: () => apiClient.get(`/contracts/${id}/derived`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation<ContractDetail, Error, CreateContractInput>({
    mutationFn: (input) => apiClient.post('/contracts', input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

export function useUpdateContract(id: string) {
  const qc = useQueryClient()
  return useMutation<ContractDetail, Error, UpdateContractInput>({
    mutationFn: (input) => apiClient.patch(`/contracts/${id}`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

export function useDeleteContract() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/contracts/${id}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

// ─── ESET Booking ───────────────────────────────────────────────────────────

export interface OrderEsetResult {
  contractId: string
  esetCustomerId: string
  orderedItems: Array<{
    contractItemId: string
    productCode: string
    quantity: number
    publicLicenseKey: string
  }>
  skippedItems: Array<{ contractItemId: string; reason: string }>
}

export function useOrderContractAtEset(contractId: string) {
  const qc = useQueryClient()
  return useMutation<OrderEsetResult, Error, void>({
    mutationFn: () =>
      apiClient.post(`/eset/contracts/${contractId}/order`, {}).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

// ─── Contract Links ─────────────────────────────────────────────────────────

export interface CreateContractLinkInput {
  contractId: string
  kind?: 'document' | 'external_url' | 'sap_ref' | 'tanss_ref' | 'other'
  label?: string
  url?: string
}

export function useAddContractLink() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, CreateContractLinkInput>({
    mutationFn: ({ contractId, ...rest }) =>
      apiClient.post(`/contracts/${contractId}/links`, rest).then((r) => r.data),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['contracts', 'detail', vars.contractId] })
    },
  })
}

export function useRemoveContractLink() {
  const qc = useQueryClient()
  return useMutation<void, Error, { contractId: string; linkId: string }>({
    mutationFn: ({ contractId, linkId }) =>
      apiClient.delete(`/contracts/${contractId}/links/${linkId}`).then(() => undefined),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['contracts', 'detail', vars.contractId] })
    },
  })
}

// ─── Products ───────────────────────────────────────────────────────────────

export interface ProductsFilter {
  category?: string
  active?: boolean
  q?: string
  /** UUID of a contract group or 'none' for unassigned products. */
  contractGroupId?: string
}

export function useProducts(filter: ProductsFilter = {}) {
  return useQuery<Product[]>({
    queryKey: ['products', 'list', filter],
    queryFn: () =>
      apiClient
        .get('/products', {
          params: {
            ...(filter.category ? { category: filter.category } : {}),
            ...(typeof filter.active === 'boolean' ? { active: String(filter.active) } : {}),
            ...(filter.q ? { q: filter.q } : {}),
            ...(filter.contractGroupId ? { contractGroupId: filter.contractGroupId } : {}),
          },
        })
        .then((r) => r.data),
  })
}

export function useProduct(id: string | undefined) {
  return useQuery<ProductDetail>({
    queryKey: ['products', 'detail', id],
    queryFn: () => apiClient.get(`/products/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation<Product, Error, CreateProductInput>({
    mutationFn: (input) => apiClient.post('/products', input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient()
  return useMutation<Product, Error, UpdateProductInput>({
    mutationFn: (input) => apiClient.patch(`/products/${id}`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/products/${id}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

// ─── Customer / Supplier Picker ─────────────────────────────────────────────

export function useCustomers(search: string) {
  return useQuery<CustomerLite[]>({
    queryKey: ['customers', 'search', search],
    queryFn: () => apiClient.get('/customers', { params: { search } }).then((r) => r.data),
    placeholderData: (prev) => prev,
  })
}

export function useSapBusinessPartners(search: string) {
  const tenantId = useTenantStore((s) => s.activeTenantId)
  return useQuery<SapBusinessPartner[]>({
    queryKey: ['sap', 'partners', search, tenantId],
    queryFn: () =>
      apiClient.get('/sap/partners', { params: { search } }).then((r) => r.data),
    enabled: search.length >= 2,
    placeholderData: (prev) => prev,
  })
}

// Searches the local `articles` table. SAP is the leading system; the `sap-article-sync` cron
// keeps the local master data up to date (see jobs/queue.ts).
export function useArticles(search: string) {
  return useQuery<ArticlesPage>({
    queryKey: ['articles', 'search', search],
    queryFn: () =>
      apiClient
        .get('/articles', { params: { q: search, pageSize: 25 } })
        .then((r) => r.data),
    enabled: search.length >= 2,
    placeholderData: (prev) => prev,
  })
}

// ─── Tariffs ────────────────────────────────────────────────────────────────

export interface TariffsFilter {
  productId?: string
  active?: boolean
}

export function useTariffs(filter: TariffsFilter = {}) {
  return useQuery<Tariff[]>({
    queryKey: ['tariffs', 'list', filter],
    queryFn: () =>
      apiClient
        .get('/tariffs', {
          params: {
            ...(filter.productId ? { productId: filter.productId } : {}),
            ...(typeof filter.active === 'boolean' ? { active: String(filter.active) } : {}),
          },
        })
        .then((r) => r.data),
  })
}

export function useTariff(id: string | undefined) {
  return useQuery<TariffDetail>({
    queryKey: ['tariffs', 'detail', id],
    queryFn: () => apiClient.get(`/tariffs/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

function invalidateTariffs(qc: ReturnType<typeof useQueryClient>, tariffId?: string) {
  void qc.invalidateQueries({ queryKey: ['tariffs'] })
  if (tariffId) void qc.invalidateQueries({ queryKey: ['tariffs', 'detail', tariffId] })
}

export function useCreateTariff() {
  const qc = useQueryClient()
  return useMutation<Tariff, Error, CreateTariffInput>({
    mutationFn: (input) => apiClient.post('/tariffs', input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc),
  })
}

export function useUpdateTariff(id: string) {
  const qc = useQueryClient()
  return useMutation<Tariff, Error, UpdateTariffInput>({
    mutationFn: (input) => apiClient.patch(`/tariffs/${id}`, input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc, id),
  })
}

export function useDeleteTariff() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/tariffs/${id}`).then(() => undefined),
    onSuccess: () => invalidateTariffs(qc),
  })
}

export function useAddTariffComponent(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<TariffComponent, Error, CreateTariffComponentInput>({
    mutationFn: (input) =>
      apiClient.post(`/tariffs/${tariffId}/components`, input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

export function useUpdateTariffComponent(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<TariffComponent, Error, { componentId: string; input: UpdateTariffComponentInput }>({
    mutationFn: ({ componentId, input }) =>
      apiClient.patch(`/tariffs/components/${componentId}`, input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

export function useDeleteTariffComponent(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (componentId) =>
      apiClient.delete(`/tariffs/components/${componentId}`).then(() => undefined),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

export function useAddPricingTier(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<PricingTier, Error, { componentId: string; input: CreatePricingTierInput }>({
    mutationFn: ({ componentId, input }) =>
      apiClient.post(`/tariffs/components/${componentId}/tiers`, input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

export function useUpdatePricingTier(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<PricingTier, Error, { tierId: string; input: UpdatePricingTierInput }>({
    mutationFn: ({ tierId, input }) =>
      apiClient.patch(`/tariffs/tiers/${tierId}`, input).then((r) => r.data),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

export function useDeletePricingTier(tariffId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (tierId) => apiClient.delete(`/tariffs/tiers/${tierId}`).then(() => undefined),
    onSuccess: () => invalidateTariffs(qc, tariffId),
  })
}

// ─── Contract Items + Sub-Records ────────────────────────────────────────────

function invalidateContract(qc: ReturnType<typeof useQueryClient>, contractId: string) {
  void qc.invalidateQueries({ queryKey: ['contracts', 'detail', contractId] })
}

export function useAddContractItem(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractItem, Error, CreateContractItemInput>({
    mutationFn: (input) => apiClient.post(`/contracts/${contractId}/items`, input).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useUpdateContractItem(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractItem, Error, { itemId: string; input: UpdateContractItemInput }>({
    mutationFn: ({ itemId, input }) =>
      apiClient.patch(`/contracts/items/${itemId}`, input).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useDeleteContractItem(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (itemId) => apiClient.delete(`/contracts/items/${itemId}`).then(() => undefined),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useInitItemsFromTariff(contractId: string) {
  const qc = useQueryClient()
  return useMutation<{ added: number; ids: string[] }, Error, void>({
    mutationFn: () => apiClient.post(`/contracts/${contractId}/items/from-tariff`, {}).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useAddItemUser(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractItemUser, Error, { itemId: string; input: AddContractItemUserInput }>({
    mutationFn: ({ itemId, input }) =>
      apiClient.post(`/contracts/items/${itemId}/users`, input).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useUpdateItemUserType(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractItemUser, Error, { itemId: string; userId: string; userType: ContractItemUserType }>({
    mutationFn: ({ itemId, userId, userType }) =>
      apiClient.patch(`/contracts/items/${itemId}/users/${userId}`, { userType }).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useRemoveItemUser(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, { itemId: string; userId: string }>({
    mutationFn: ({ itemId, userId }) =>
      apiClient.delete(`/contracts/items/${itemId}/users/${userId}`).then(() => undefined),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useAddItemAsset(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractItemAsset, Error, { itemId: string; input: AddContractItemAssetInput }>({
    mutationFn: ({ itemId, input }) =>
      apiClient.post(`/contracts/items/${itemId}/assets`, input).then((r) => r.data),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useRemoveItemAsset(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, { itemId: string; assetId: string }>({
    mutationFn: ({ itemId, assetId }) =>
      apiClient.delete(`/contracts/items/${itemId}/assets/${assetId}`).then(() => undefined),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useNextContractNumber() {
  return useQuery<{ next: number }>({
    queryKey: ['contracts', 'next-number'],
    queryFn: () => apiClient.get('/contracts/next-number').then((r) => r.data),
  })
}

export function useSetNextContractNumber() {
  const qc = useQueryClient()
  return useMutation<{ next: number }, Error, number>({
    mutationFn: (value) => apiClient.patch('/contracts/next-number', { value }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contracts', 'next-number'] }),
  })
}

// ─── Contract Groups (Stage 13) ─────────────────────────────────────────────

export function useContractGroups() {
  return useQuery<ContractGroup[]>({
    queryKey: ['contract-groups', 'list'],
    queryFn: () => apiClient.get('/contract-groups').then((r) => r.data),
  })
}

export function useContractGroup(id: string | undefined) {
  return useQuery<ContractGroupDetail>({
    queryKey: ['contract-groups', 'detail', id],
    queryFn: () => apiClient.get(`/contract-groups/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useCreateContractGroup() {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, CreateContractGroupInput>({
    mutationFn: (input) => apiClient.post('/contract-groups', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-groups'] }),
  })
}

export function useUpdateContractGroup(id: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, UpdateContractGroupInput>({
    mutationFn: (patch) => apiClient.patch(`/contract-groups/${id}`, patch).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-groups'] }),
  })
}

export function useDeleteContractGroup() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/contract-groups/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-groups'] }),
  })
}

// ─── Contract Versions (Stage 13) ───────────────────────────────────────────

export function useContractVersions(contractId: string | undefined) {
  return useQuery<ContractVersion[]>({
    queryKey: ['contracts', 'versions', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/versions`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

export function useCreateContractVersion(contractId: string) {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, CreateContractVersionInput>({
    mutationFn: (input) => apiClient.post(`/contracts/${contractId}/versions`, input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contracts', 'versions', contractId] }),
  })
}

export function useDeleteContractVersion(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (versionId) =>
      apiClient.delete(`/contracts/${contractId}/versions/${versionId}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contracts', 'versions', contractId] }),
  })
}

// ─── Line Item History (Stage 13c) ──────────────────────────────────────────

export interface ContractItemHistoryEntry {
  id: string
  tenantId: string
  contractItemId: string
  contractId: string
  actionType: 'added' | 'removed' | 'updated' | 'amount_changed' | 'discount_changed' | 'memo_changed' | 'lifecycle_changed'
  validFrom: string | null
  validUntil: string | null
  oldValueJson: Record<string, unknown> | null
  newValueJson: Record<string, unknown> | null
  changedById: string | null
  changedAt: string
  reason: string | null
}

export interface ContractItemPriceHistoryEntry {
  id: string
  tenantId: string
  contractItemId: string
  oldPriceCents: number | null
  newPriceCents: number
  validFrom: string
  changedById: string | null
  changedAt: string
  reason: string | null
}

export function useContractItemHistory(itemId: string | undefined) {
  return useQuery<ContractItemHistoryEntry[]>({
    queryKey: ['contract-items', 'history', itemId],
    queryFn: () => apiClient.get(`/contracts/items/${itemId}/history`).then((r) => r.data),
    enabled: Boolean(itemId),
  })
}

export function useContractItemPriceHistory(itemId: string | undefined) {
  return useQuery<ContractItemPriceHistoryEntry[]>({
    queryKey: ['contract-items', 'price-history', itemId],
    queryFn: () => apiClient.get(`/contracts/items/${itemId}/price-history`).then((r) => r.data),
    enabled: Boolean(itemId),
  })
}

// ─── User Classes per Contract (Stage 14) ───────────────────────────────────

export function useContractUserGroups(contractId: string | undefined) {
  return useQuery<ContractUserGroup[]>({
    queryKey: ['contract-user-groups', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/user-groups`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

export function useCreateContractUserGroup(contractId: string) {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, CreateContractUserGroupInput>({
    mutationFn: (input) => apiClient.post(`/contracts/${contractId}/user-groups`, input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-user-groups', contractId] }),
  })
}

export interface UpdateUserGroupResult {
  pendingReductionCreated: boolean
  effectiveAt: string | null
}

export function useUpdateContractUserGroup(contractId: string) {
  const qc = useQueryClient()
  return useMutation<UpdateUserGroupResult, Error, { id: string; patch: UpdateContractUserGroupInput }>({
    mutationFn: ({ id, patch }) =>
      apiClient.patch(`/contracts/user-groups/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-user-groups', contractId] })
      void qc.invalidateQueries({ queryKey: ['contract-pending-reductions', contractId] })
    },
  })
}

export function useDeleteContractUserGroup(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/contracts/user-groups/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-user-groups', contractId] }),
  })
}

// ─── Setup Fees ─────────────────────────────────────────────────────────────

export function useContractSetupFees(contractId: string | undefined) {
  return useQuery<SetupFeeItem[]>({
    queryKey: ['contract-setup-fees', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/setup-fees`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

// ─── TANSS Sync for User Classes (Stage 18) ──────────────────────────────────

export interface UserGroupSyncResult {
  groupsSynced: number
  groupsSkipped: number
  errors: Array<{ groupId: string; message: string }>
}

export function useSyncContractUserGroups(contractId: string) {
  const qc = useQueryClient()
  return useMutation<UserGroupSyncResult, Error, void>({
    mutationFn: () => apiClient.post(`/contracts/${contractId}/sync-user-groups`, {}).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-user-groups', contractId] }),
  })
}

export function usePendingUserGroupReductions(contractId: string | undefined) {
  return useQuery<PendingUserGroupReduction[]>({
    queryKey: ['contract-pending-reductions', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/pending-reductions`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

export function useCancelUserGroupReduction(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (reductionId) =>
      apiClient.delete(`/contracts/pending-reductions/${reductionId}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-pending-reductions', contractId] })
      void qc.invalidateQueries({ queryKey: ['contract-user-groups', contractId] })
    },
  })
}

// ─── pass-through n:m links (CODA-650) ──────────────────────────────────────

export function useContractItemLinks(contractId: string | undefined) {
  return useQuery<import('../types.ts').ContractItemLink[]>({
    queryKey: ['contract-item-links', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/item-links`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

export function useCreateContractItemLink(contractId: string) {
  const qc = useQueryClient()
  return useMutation<import('../types.ts').ContractItemLink, Error, import('../types.ts').CreateContractItemLinkInput>({
    mutationFn: (input) => apiClient.post(`/contracts/${contractId}/item-links`, input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-item-links', contractId] }),
  })
}

export function useDeleteContractItemLink(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (linkId) => apiClient.delete(`/contracts/${contractId}/item-links/${linkId}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-item-links', contractId] }),
  })
}
